import type { MessageBlock, ThreadMessage, ThreadSnapshot } from "@rakazo/contracts";
import { runThreadSubscription } from "@rakazo/core";
import { useEffect, useRef, useState } from "react";
import { rpc } from "./rpc";
import { isThreadSnapshotEvent, reduceThreadSnapshot } from "./thread-events";

/** A bot's chat: its main chat without sessionId, one of its work sessions with it. */
export type LiveThreadTarget = { botId: string; sessionId?: string | null };

/**
 * A thread live from its event stream like the main chat: the snapshot loads once, then every
 * message and the text of a running turn arrive as they are written. Null target loads nothing.
 */
export function useLiveThread(
  target: LiveThreadTarget | null,
  onChange?: () => void,
): { snapshot: ThreadSnapshot | null; failed: boolean } {
  const [snapshot, setSnapshot] = useState<ThreadSnapshot | null>(null);
  const [failed, setFailed] = useState(false);
  const changed = useRef(onChange);
  changed.current = onChange;
  const botId = target?.botId;
  const sessionId = target?.sessionId ?? undefined;
  useEffect(() => {
    if (!botId) return;
    const controller = new AbortController();
    const at = sessionId ? { botId, sessionId } : { botId };
    let current: ThreadSnapshot | null = null;
    const commit = (next: ThreadSnapshot | null) => {
      current = next;
      setSnapshot(next);
      return next;
    };
    const load = async () => {
      try {
        const next = commit(await rpc.threads.get(at, { signal: controller.signal }));
        setFailed(false);
        return next;
      } catch (error) {
        if (!controller.signal.aborted) setFailed(true);
        throw error;
      }
    };
    setSnapshot(null);
    void runThreadSubscription({
      signal: controller.signal,
      loadInitial: load,
      loadHead: () => rpc.threads.head(at, { signal: controller.signal }),
      refresh: load,
      currentSnapshot: () => current,
      subscribe: (cursor, signal) => rpc.threads.subscribe({ ...at, cursor }, { signal }),
      applyEvent: (event) => {
        if (isThreadSnapshotEvent(event)) commit(reduceThreadSnapshot(current, event));
      },
      onEvent: (event) => {
        if (event.type.startsWith("run.") || event.type === "thread.message.created")
          changed.current?.();
      },
    });
    return () => controller.abort();
  }, [botId, sessionId]);
  return { snapshot, failed };
}

/** The readable text of one message block, or null for blocks a plain chat view leaves out. */
function blockText(block: MessageBlock): { text: string; from?: string } | null {
  if (block.kind === "text") return block.text.trim() ? { text: block.text } : null;
  if (block.kind === "bot_message_received") return { text: block.text, from: block.fromBotName };
  if (block.kind === "bot_message_sent") return { text: block.text, from: `→ ${block.toBotName}` };
  return null;
}

/** One bubble of a plain chat view. */
export type ThreadRow = { key: string; role: ThreadMessage["role"]; text: string; from?: string };

/** Messages as bubbles, text blocks only; `since` (ISO) drops older messages. */
export function threadRows(messages: readonly ThreadMessage[], since?: string): ThreadRow[] {
  const from = since ? Date.parse(since) : Number.NaN;
  return messages.flatMap((message) => {
    if (!Number.isNaN(from) && Date.parse(message.createdAt) < from) return [];
    return message.blocks.flatMap((block, index) => {
      const shown = blockText(block);
      return shown ? [{ key: `${message.id}:${index}`, role: message.role, ...shown }] : [];
    });
  });
}
